import React, { useEffect, useState } from "react";
import { api } from "../../lib/apiClient";
import { Edit3, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "../../components/ui/sonner";
import { useConfirm } from "../../components/ConfirmDialog";
import { SimpleDialog, Input } from "./_shared";
import { SupplierUsageReport } from "./SupplierUsageReport";

const TYPES = [["materials", "Parts & Materials"], ["access_hire", "Access Hire"]];
const EMPTY = { name: "", aliases: [], contact_name: "", email: "", phone: "", notes: "", types: ["materials"], categories: [], all_categories: false, active: true };
const CHK = "h-4 w-4 accent-slate-900";
const err = (e, f) => toast.error(e?.response?.data?.detail || f);

const Check = ({ label, checked, onChange, testid }) => (
  <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
    <input type="checkbox" className={CHK} checked={!!checked} onChange={(e) => onChange(e.target.checked)} data-testid={testid} />{label}
  </label>
);

const toggleIn = (list, v, on) => (on ? [...new Set([...list, v])] : list.filter((x) => x !== v));

function WarningSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/supplier-settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = (next = s, msg = "Warning wording saved.") => api.put("/supplier-settings", next).then(() => toast.success(msg)).catch((e) => err(e, "Could not save"));
  const toggle = (on) => { const next = { ...s, enabled: on }; setS(next); save(next, on ? "Supplier checks turned on." : "Supplier checks turned off."); };
  return (
    <div className="qb-card p-4 space-y-3" data-testid="supplier-warning-settings">
      <label className={`flex items-center justify-between gap-3 p-3 rounded border ${s.enabled ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
        <span>
          <span className="block text-sm font-semibold text-slate-900">Approved supplier checks {s.enabled ? "ON" : "OFF"}</span>
          <span className="block text-xs text-slate-500">When off, estimators don't see the approved suppliers box or the "not approved" pop-up.</span>
        </span>
        <input type="checkbox" className="h-5 w-5 accent-emerald-600" checked={!!s.enabled} onChange={(e) => toggle(e.target.checked)} data-testid="supplier-feature-toggle" />
      </label>
      <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-800">"Not approved" pop-up wording</div>
      <input value={s.warning_title} onChange={(e) => setS({ ...s, warning_title: e.target.value })} data-testid="supplier-warning-title"
        className="h-9 w-full border border-slate-300 px-2 text-sm font-semibold" />
      <textarea rows={3} value={s.warning_message} onChange={(e) => setS({ ...s, warning_message: e.target.value })} data-testid="supplier-warning-message"
        className="w-full border border-slate-300 px-2 py-1.5 text-sm" />
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs text-slate-500">Use <code>{"{supplier}"}</code> and <code>{"{category}"}</code> to insert the supplier name and quote category.</span>
        <button onClick={() => save()} data-testid="supplier-warning-save" className="h-9 px-4 rounded-full bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark">
          <Save size={14} /> Save wording
        </button>
      </div>
    </div>
  );
}

function SupplierDialog({ value, categories, onClose, onSaved }) {
  const [d, setD] = useState({ ...EMPTY, ...value });
  const [aliasText, setAliasText] = useState((value.aliases || []).join(", "));
  const set = (k) => (v) => setD((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      const body = { ...d, aliases: aliasText.split(",").map((a) => a.trim()).filter(Boolean) };
      if (d.id) await api.put(`/suppliers/${d.id}`, body);
      else await api.post("/suppliers", body);
      onSaved();
    } catch (e) { err(e, "Could not save supplier"); }
  };
  return (
    <SimpleDialog title={d.id ? "Edit supplier" : "Add supplier"} onClose={onClose} onSave={save}>
      <div data-testid="supplier-dialog" className="space-y-3">
        <Input label="Supplier name *" value={d.name} set={set("name")} />
        <Input label="Other spellings (comma separated)" value={aliasText} set={setAliasText} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Contact name" value={d.contact_name} set={set("contact_name")} />
          <Input label="Phone" value={d.phone} set={set("phone")} />
        </div>
        <Input label="Email" type="email" value={d.email} set={set("email")} />
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-600 mb-1 block">Notes</label>
          <textarea rows={2} value={d.notes} onChange={(e) => set("notes")(e.target.value)} className="w-full border border-slate-300 px-2 py-1 text-sm" />
        </div>
        <div className="flex gap-4">{TYPES.map(([k, l]) => <Check key={k} label={l} checked={d.types.includes(k)} testid={`supplier-type-${k}`} onChange={(on) => set("types")(toggleIn(d.types, k, on))} />)}</div>
        <div className="flex flex-col gap-1.5">
          <Check label="Approved for all categories" checked={d.all_categories} onChange={set("all_categories")} testid="supplier-all-categories" />
          <Check label="Active" checked={d.active} onChange={set("active")} testid="supplier-active" />
        </div>
        {!d.all_categories && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-1">Approved categories</div>
            <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto border border-slate-200 rounded p-2">
              {categories.map((c) => <Check key={c.id} label={c.name} checked={d.categories.includes(c.name)} testid={`supplier-cat-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                onChange={(on) => set("categories")(toggleIn(d.categories, c.name, on))} />)}
            </div>
          </div>
        )}
      </div>
    </SimpleDialog>
  );
}

const typeLabel = (t) => TYPES.filter(([k]) => t.includes(k)).map(([, l]) => l).join(", ");

export function SuppliersTab() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [reportKey, setReportKey] = useState(0);
  const confirm = useConfirm();
  const load = () => api.get("/suppliers").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/quote-categories").then((r) => setCats(r.data)); }, []);

  const patch = (s, p) => api.put(`/suppliers/${s.id}`, { ...s, ...p }).then(load).catch((e) => err(e, "Could not update"));
  const remove = async (s) => {
    if (!(await confirm({ title: `Delete ${s.name}?`, description: "It will no longer count as approved on new quotes.", confirmText: "Delete", destructive: true }))) return;
    await api.delete(`/suppliers/${s.id}`);
    load();
  };

  return (
    <div className="space-y-5" data-testid="suppliers-tab">
      <WarningSettings />
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Parts & Materials and Access Hire lines are checked against this list.</p>
        <button onClick={() => setEditing({})} data-testid="supplier-add-btn" className="h-9 px-4 rounded-full bg-slate-900 text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2">
          <Plus size={14} /> Add supplier
        </button>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg bg-white">
        <table className="min-w-full text-sm" data-testid="suppliers-table">
          <thead className="bg-slate-50"><tr>
            {["Supplier", "Contact", "Type", "Approved for", "Approved for all categories", ""].map((h) => <th key={h} className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">{h}</th>)}
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No suppliers yet.</td></tr>}
            {items.map((s) => (
              <tr key={s.id} className={`border-t border-slate-100 align-top ${s.active === false ? "opacity-50" : ""}`} data-testid={`supplier-row-${s.id}`}>
                <td className="px-3 py-2 font-semibold">{s.name}{s.aliases?.length ? <div className="text-xs font-normal text-slate-500">aka {s.aliases.join(", ")}</div> : null}</td>
                <td className="px-3 py-2 text-xs">{s.contact_name}<div>{s.email}</div><div>{s.phone}</div></td>
                <td className="px-3 py-2 text-xs">{typeLabel(s.types || [])}</td>
                <td className="px-3 py-2 text-xs max-w-xs">{s.all_categories ? <span className="font-semibold">All categories</span> : (s.categories || []).join(", ") || "—"}</td>
                <td className="px-3 py-2">
                  <input type="checkbox" className={CHK} checked={!!s.all_categories} onChange={(e) => patch(s, { all_categories: e.target.checked })} data-testid={`supplier-all-toggle-${s.id}`} />
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => setEditing(s)} className="p-1 text-slate-500 hover:text-slate-900" data-testid={`supplier-edit-${s.id}`}><Edit3 size={14} /></button>
                  <button onClick={() => remove(s)} className="p-1 text-red-500 hover:text-red-700" data-testid={`supplier-delete-${s.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <SupplierUsageReport
        refreshKey={reportKey}
        suppliers={items}
        onAdd={(prefill) => setEditing(prefill)}
        onApprove={async (id, category) => {
          const s = items.find((x) => x.id === id);
          if (!s) return;
          await patch(s, { categories: [...new Set([...(s.categories || []), category])] });
          toast.success(`${s.name} approved for ${category}.`);
          setReportKey((k) => k + 1);
        }}
      />
      {editing && <SupplierDialog value={editing} categories={cats} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); setReportKey((k) => k + 1); toast.success("Supplier saved."); }} />}
    </div>
  );
}
